import { LOCALE_DISPLAY_NAMES, normalizeLocale, type Locale } from '@/lib/locale'

/** Locales available on the international landing page (Persian uses /home). */
export const LANDING_LOCALES = ['en', 'hy', 'ru'] as const
export type LandingLocale = (typeof LANDING_LOCALES)[number]

export const LANDING_LOCALE_STORAGE_KEY = 'atiram-landing-locale'

export const PERSIAN_HOME_PATH = '/home'

/** International landing v2 is served at the site root (and also at /landing2). */
export const INTERNATIONAL_LANDING_PATH = '/'

export const LANDING2_LOCALES = ['en', 'hy'] as const
export type Landing2MarketingLocale = (typeof LANDING2_LOCALES)[number]

export function isLanding2MarketingLocale(value: string): value is Landing2MarketingLocale {
  return (LANDING2_LOCALES as readonly string[]).includes(value)
}

export function internationalLandingPath(locale: Landing2MarketingLocale): string {
  return `${INTERNATIONAL_LANDING_PATH}?lang=${locale}`
}

export function readLocaleFromSearchParams(search: string): Landing2MarketingLocale | null {
  const lang = new URLSearchParams(search).get('lang')
  return lang && isLanding2MarketingLocale(lang) ? lang : null
}

export function isLandingLocale(value: string): value is LandingLocale {
  return (LANDING_LOCALES as readonly string[]).includes(value)
}

export function getLandingLocaleDisplayName(locale: LandingLocale): string {
  return LOCALE_DISPLAY_NAMES[locale as Locale]
}

/** Pricing JSON exists for en/fa only; international landing uses English plans. */
export function getLandingPricingLocale(_locale: LandingLocale): 'en' | 'fa' {
  return 'en'
}

export type LandingContact = {
  phones: Array<{ display: string; tel: string }>
}

/** Shared contact for all international landing locales (Persian uses /home). */
export const INTERNATIONAL_CONTACT: LandingContact = {
  phones: [{ display: '+374-98318980', tel: '+37498318980' }],
}

export function getLandingContact(_locale: LandingLocale): LandingContact {
  return INTERNATIONAL_CONTACT
}

export function detectLandingLocaleFromNavigator(): LandingLocale {
  if (typeof navigator === 'undefined') return 'en'

  const langs = [navigator.language, ...(navigator.languages ?? [])]
  for (const lang of langs) {
    const normalized = normalizeLocale(lang)
    if (normalized && isLandingLocale(normalized)) {
      return normalized
    }
  }

  return 'en'
}

export function readStoredLandingLocale(): LandingLocale | null {
  if (typeof window === 'undefined') return null
  const stored = window.localStorage.getItem(LANDING_LOCALE_STORAGE_KEY)
  return stored && isLandingLocale(stored) ? stored : null
}

export function storeLandingLocale(locale: LandingLocale): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(LANDING_LOCALE_STORAGE_KEY, locale)
}

export function resolveInitialLandingLocale(): LandingLocale {
  return readStoredLandingLocale() ?? detectLandingLocaleFromNavigator()
}
